import { useLocation } from "wouter"
import { cn } from "@/lib/utils"
import { useStudioStore } from "@/store/studio"
import { Clapperboard, Film, Sparkles, Radio, CheckCircle, Settings, Plus, Cloud } from "lucide-react"
import { useAuth } from "@clerk/clerk-react"

const navItems = [
  { key: "home", label: "Overview", icon: Sparkles },
  { key: "develop", label: "Develop", icon: Clapperboard, stage: 1 },
  { key: "plan", label: "Plan", icon: Film, stage: 2 },
  { key: "produce", label: "Produce", icon: CheckCircle, stage: 3 },
  { key: "finish", label: "Finish", icon: Cloud, stage: 4 },
  { key: "publish", label: "Publish", icon: Radio, stage: 5 },
]

function getStageStatus(store: ReturnType<typeof useStudioStore.getState>, stage: number) {
  if (store.stage > stage) return "complete"
  if (store.stage === stage) return "ready"
  return "waiting"
}

export default function Sidebar() {
  const [location, navigate] = useLocation()
  const store = useStudioStore()
  const { isLoaded, isSignedIn } = useAuth()
  const currentTab = location.replace("/studio/", "") || "home"

  const go = (key: string) => navigate(key === "home" ? "/studio" : `/studio/${key}`)

  return (<>
    <aside className="hidden lg:flex w-56 shrink-0 border-r border-[#1e2130] bg-[#08090d] flex-col h-[calc(100vh-3.5rem)] sticky top-14">
      <div className="p-3">
        <button
          onClick={() => {
            store.resetProject()
            navigate("/studio")
          }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0f1117] border border-[#1e2130] hover:border-[#2d3148] text-sm font-medium text-[#f0f1f5] transition-colors"
        >
          <Plus className="w-4 h-4" />
          New project
        </button>
      </div>

      <nav className="flex-1 px-2 space-y-0.5">
        <p className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#6b7280]">Production</p>
        {navItems.map((item) => {
          const isActive = currentTab === item.key
          const status = item.stage ? getStageStatus(store, item.stage) : null

          return (
            <button
              key={item.key}
              onClick={() => go(item.key)}
              className={cn(
                "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors",
                isActive
                  ? "bg-[#f59e0b]/10 text-[#f59e0b] font-medium"
                  : "text-[#9ca3af] hover:bg-[#0f1117] hover:text-[#f0f1f5]"
              )}
            >
              <item.icon className={cn("w-4 h-4", isActive && "text-[#f59e0b]")} />
              <span className="flex-1 text-left">{item.label}</span>
              {status === "complete" && <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />}
            </button>
          )
        })}
      </nav>

      <div className="p-3 border-t border-[#1e2130]">
        <button
          onClick={() => navigate("/dashboard")}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-[#9ca3af] hover:bg-[#0f1117] hover:text-[#f0f1f5] transition-colors"
        >
          <Settings className="w-4 h-4" />
          Dashboard
        </button>
        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0f1117] border border-[#1e2130]">
          <Cloud className="w-3.5 h-3.5 text-[#10b981]" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-medium text-[#f0f1f5]">Account & rendering</p>
            <p className="text-[10px] text-[#6b7280]">{store.identity ? "Signed in" : isLoaded && isSignedIn ? "Connecting…" : "Signed out"}</p>
          </div>
          <span className={cn("w-1.5 h-1.5 rounded-full", store.identity ? "bg-[#10b981]" : isLoaded && isSignedIn ? "bg-[#f59e0b] animate-pulse" : "bg-[#ef4444]")} />
        </div>
      </div>
    </aside>
    <nav aria-label="Production stages" className="lg:hidden fixed inset-x-0 bottom-0 z-[70] h-[4.5rem] border-t border-[#1e2130] bg-[#08090d]/95 backdrop-blur-xl px-1 pb-[env(safe-area-inset-bottom)] flex items-stretch">
      {navItems.map((item) => {
        const isActive = currentTab === item.key
        return <button key={item.key} onClick={() => go(item.key)} className={cn("flex-1 min-w-0 flex flex-col items-center justify-center gap-1 rounded-lg text-[9px] font-medium", isActive ? "text-[#f59e0b] bg-[#f59e0b]/10" : "text-[#6b7280]")}><item.icon className="w-4 h-4" /><span className="truncate max-w-full">{item.label}</span></button>
      })}
    </nav>
  </>)
}
